import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * Small floating card offering "Install Dubinko app" on supported devices.
 * Uses beforeinstallprompt event (Chrome/Edge/Samsung Internet).
 * On iOS Safari (no such API), shows a hint how to add-to-home-screen.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('dubinko_install_dismissed');
    if (dismissed) return;

    // Do not show on admin area
    if (window.location.pathname.startsWith('/admin')) return;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // slight delay so it doesn't jump in on first paint
      setTimeout(() => setVisible(true), 8000);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS Safari fallback
    const ua = window.navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
    if (isIOS) {
      setTimeout(() => { setIosHint(true); setVisible(true); }, 10000);
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === 'accepted') {
      setVisible(false);
    }
    localStorage.setItem('dubinko_install_dismissed', '1');
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem('dubinko_install_dismissed', '1');
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-5 sm:w-[340px] z-40 mascot-pop">
      <div className="glass rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
               style={{ background: 'linear-gradient(135deg, #023E8A 0%, #5B21B6 100%)' }}>
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-slate-900">Instaliraj Dubinko app</div>
            {iosHint ? (
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Na iPhone-u dodirni <strong>Share</strong> ikonicu u Safari-ju, pa <strong>„Add to Home Screen"</strong>. Dobijaš aplikaciju bez prolaza kroz App Store.
              </p>
            ) : (
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Instaliraj sajt na početni ekran telefona — brže učitavanje, radi i bez interneta.
              </p>
            )}
            <div className="mt-3 flex items-center gap-2">
              {!iosHint && (
                <button onClick={install}
                  className="btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Instaliraj
                </button>
              )}
              <button onClick={dismiss}
                className="btn-ghost px-3 py-1.5 rounded-full text-xs font-semibold">
                Kasnije
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Zatvori"
            className="w-7 h-7 rounded-full hover:bg-slate-200 flex items-center justify-center">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
